import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-lg)] border border-[var(--cor-borda)] bg-[var(--cor-superficie)] shadow-[var(--shadow-sm)]',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  titulo,
  descricao,
  acao,
  className,
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  acao?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-[var(--cor-borda)] px-5 py-4',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-[var(--cor-texto)]">{titulo}</h2>
        {descricao ? (
          <p className="mt-0.5 text-sm text-[var(--cor-texto-suave)]">{descricao}</p>
        ) : null}
      </div>
      {acao}
    </div>
  );
}

const badge = cva(
  'inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-1 text-xs font-medium',
  {
    variants: {
      status: {
        rascunho: 'bg-[var(--status-rascunho-fundo)] text-[var(--status-rascunho)]',
        convocado: 'bg-[var(--status-convocado-fundo)] text-[var(--status-convocado)]',
        aguardando: 'bg-[var(--status-aguardando-fundo)] text-[var(--status-aguardando)]',
        confirmado: 'bg-[var(--status-confirmado-fundo)] text-[var(--status-confirmado)]',
        recusado: 'bg-[var(--status-recusado-fundo)] text-[var(--status-recusado)]',
        /*
         * Vaga aberta não é recusa. Até aqui as duas dividiam o vermelho, e o
         * produto ensinava a liderança a ver erro onde há convite: "recusado" é
         * uma resposta que alguém deu, "vaga" é um lugar esperando alguém.
         */
        vaga: 'bg-[var(--status-vaga-fundo)] text-[var(--status-vaga)]',
        substituido: 'bg-[var(--status-substituido-fundo)] text-[var(--status-substituido)]',
        neutro: 'bg-[var(--cor-superficie-2)] text-[var(--cor-texto-suave)]',
        marca: 'bg-[var(--brand-suave)] text-[var(--brand-contraste)]',
      },
    },
    defaultVariants: { status: 'neutro' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badge> {}

export function Badge({ className, status, ...props }: BadgeProps) {
  return <span className={cn(badge({ status }), className)} {...props} />;
}

/** Nada de tela em branco: todo estado vazio ensina o próximo passo (seção 9.1). */
export function EmptyState({
  icone,
  titulo,
  descricao,
  acao,
}: {
  icone?: ReactNode;
  titulo: string;
  descricao: string;
  acao?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      {icone ? (
        <div className="flex size-12 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--brand-suave)] text-[var(--brand-contraste)]">
          {icone}
        </div>
      ) : null}
      <h3 className="text-base font-semibold text-[var(--cor-texto)]">{titulo}</h3>
      <p className="max-w-sm text-sm text-[var(--cor-texto-suave)]">{descricao}</p>
      {acao ? <div className="mt-1">{acao}</div> : null}
    </div>
  );
}

/** Skeleton no lugar de spinner: a tela já mostra a forma do que vem (seção 9.1). */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded-[var(--radius-sm)] bg-[var(--cor-superficie-2)]', className)}
    />
  );
}
